import { inject, Injectable } from '@angular/core';
import type { FamilyContextResponse } from '@nexus/api-types';
import type {
  WishlistCreateItemPriority,
  WishlistCreateOccasion,
  WishlistCreateStep,
  WishlistVisibility,
} from '@nexus/customer/wishlists/model';
import {
  WISHLIST_PERSISTENT_MEMORY_KEY_PREFIX,
  WISHLIST_PERSISTENT_MEMORY_VERSION,
} from '../constants/wishlist-persistent-memory.constants';
import { WISHLIST_PERSISTENT_MEMORY_STORE } from '../constants/wishlist-persistent-memory.tokens';
import type { WishlistCreatePersistentDraft } from '../types/wishlist-create-persistent-draft.interface';
import type { WishlistCreatePersistentItem } from '../types/wishlist-create-persistent-item.interface';
import type { WishlistCreatePersistentMemory } from '../types/wishlist-create-persistent-memory.interface';
import type { WishlistOverviewPersistentMemory } from '../types/wishlist-overview-persistent-memory.interface';
import type { WishlistOverviewSectionMemoryKey } from '../types/wishlist-overview-section-memory-key.type';
import type { WishlistPersistentMemoryContext } from '../types/wishlist-persistent-memory-context.interface';
import type { WishlistPersistentMemoryStore } from '../types/wishlist-persistent-memory-store.interface';
import type { WishlistPersistentMemorySurface } from '../types/wishlist-persistent-memory-surface.type';
import { WishlistIndexedDbPersistentMemoryStoreService } from './wishlist-indexeddb-persistent-memory-store.service';

const WISHLIST_CREATE_STEPS: readonly WishlistCreateStep[] = [
  'basic',
  'beneficiary',
  'visibility',
  'items',
  'summary',
];
const WISHLIST_VISIBILITIES: readonly WishlistVisibility[] = [
  'private',
  'household',
  'invited_only',
  'anyone_with_link',
];
const WISHLIST_CREATE_OCCASIONS: readonly WishlistCreateOccasion[] = [
  'birthday',
  'holidays',
  'layette',
  'sport',
  'home',
  'none',
  'custom',
];
const WISHLIST_ITEM_PRIORITIES: readonly WishlistCreateItemPriority[] = ['low', 'medium', 'high'];
const WISHLIST_OVERVIEW_SECTIONS: readonly WishlistOverviewSectionMemoryKey[] = ['mine', 'other'];

@Injectable({ providedIn: 'root' })
export class WishlistPersistentMemoryService {
  private readonly fallbackStore: WishlistIndexedDbPersistentMemoryStoreService = inject(
    WishlistIndexedDbPersistentMemoryStoreService,
  );
  private readonly injectedStore: WishlistPersistentMemoryStore | null = inject(WISHLIST_PERSISTENT_MEMORY_STORE, {
    optional: true,
  });
  private readonly store: WishlistPersistentMemoryStore = this.injectedStore ?? this.fallbackStore;

  public createContextFromFamilyContext(
    familyContext: FamilyContextResponse,
    surface: WishlistPersistentMemorySurface,
  ): WishlistPersistentMemoryContext | null {
    const householdId: string = familyContext.householdId.trim();
    const userProfileId: string = (familyContext.currentUserProfileId ?? familyContext.activeProfileId ?? '').trim();

    if (householdId.length === 0 || userProfileId.length === 0) {
      return null;
    }

    return {
      householdId,
      userProfileId,
      activeProfileId: familyContext.activeProfileId?.trim() || null,
      surface,
    };
  }

  public createMemoryKey(context: WishlistPersistentMemoryContext): string {
    return [
      WISHLIST_PERSISTENT_MEMORY_KEY_PREFIX,
      `v${String(WISHLIST_PERSISTENT_MEMORY_VERSION)}`,
      context.surface,
      `household:${encodeURIComponent(context.householdId)}`,
      `user-profile:${encodeURIComponent(context.userProfileId)}`,
      `active-profile:${encodeURIComponent(context.activeProfileId ?? 'none')}`,
    ].join(':');
  }

  public async loadOverviewMemory(
    context: WishlistPersistentMemoryContext,
  ): Promise<WishlistOverviewPersistentMemory | null> {
    const key: string = this.createMemoryKey(context);
    const value: unknown | null = await this.readValue(key);
    const memory: WishlistOverviewPersistentMemory | null = toOverviewMemory(value);

    if (value !== null && memory === null) {
      await this.removeValue(key);
    }

    return memory;
  }

  public async saveOverviewMemory(
    context: WishlistPersistentMemoryContext,
    memory: WishlistOverviewPersistentMemory,
  ): Promise<void> {
    const sanitizedMemory: WishlistOverviewPersistentMemory | null = toOverviewMemory(memory);

    if (sanitizedMemory === null) {
      return;
    }

    await this.writeValue(this.createMemoryKey(context), sanitizedMemory);
  }

  public async loadCreateMemory(
    context: WishlistPersistentMemoryContext,
  ): Promise<WishlistCreatePersistentMemory | null> {
    const key: string = this.createMemoryKey(context);
    const value: unknown | null = await this.readValue(key);
    const memory: WishlistCreatePersistentMemory | null = toCreateMemory(value);

    if (value !== null && memory === null) {
      await this.removeValue(key);
    }

    return memory;
  }

  public async saveCreateMemory(
    context: WishlistPersistentMemoryContext,
    memory: WishlistCreatePersistentMemory,
  ): Promise<void> {
    const sanitizedMemory: WishlistCreatePersistentMemory | null = toCreateMemory(memory);

    if (sanitizedMemory === null) {
      return;
    }

    await this.writeValue(this.createMemoryKey(context), sanitizedMemory);
  }

  public async clearCreateMemory(context: WishlistPersistentMemoryContext): Promise<void> {
    await this.removeValue(this.createMemoryKey(context));
  }

  private async readValue(key: string): Promise<unknown | null> {
    try {
      return await this.store.getItem(key);
    } catch {
      return null;
    }
  }

  private async writeValue(key: string, value: unknown): Promise<void> {
    try {
      await this.store.setItem(key, value);
    } catch {
      return;
    }
  }

  private async removeValue(key: string): Promise<void> {
    try {
      await this.store.removeItem(key);
    } catch {
      return;
    }
  }
}

function toOverviewMemory(value: unknown): WishlistOverviewPersistentMemory | null {
  if (!isRecord(value) || value['version'] !== WISHLIST_PERSISTENT_MEMORY_VERSION) {
    return null;
  }

  const lastOpenedSection: WishlistOverviewSectionMemoryKey | null = readOverviewSection(value['lastOpenedSection']);

  if (lastOpenedSection === null) {
    return null;
  }

  return {
    version: WISHLIST_PERSISTENT_MEMORY_VERSION,
    lastOpenedSection,
  };
}

function toCreateMemory(value: unknown): WishlistCreatePersistentMemory | null {
  if (!isRecord(value) || value['version'] !== WISHLIST_PERSISTENT_MEMORY_VERSION) {
    return null;
  }

  const draft: WishlistCreatePersistentDraft | null = toCreateDraft(value['draft']);

  if (draft === null) {
    return null;
  }

  const idempotencyKey: string | null = typeof value['idempotencyKey'] === 'string' &&
    /^[A-Za-z0-9_-]{1,128}$/.test(value['idempotencyKey']) ? value['idempotencyKey'] : null;
  return {
    version: WISHLIST_PERSISTENT_MEMORY_VERSION,
    ...(idempotencyKey === null ? {} : { idempotencyKey }),
    ...(idempotencyKey !== null && value['pendingSubmission'] === true ? { pendingSubmission: true } : {}),
    activeStep: readCreateStep(value['activeStep']) ?? 'basic',
    draft,
  };
}

function toCreateDraft(value: unknown): WishlistCreatePersistentDraft | null {
  if (!isRecord(value)) {
    return null;
  }

  return {
    title: readString(value['title']),
    description: readString(value['description']),
    occasion: readCreateOccasion(value['occasion']),
    customOccasionLabel: readString(value['customOccasionLabel']),
    occasionDate: readString(value['occasionDate']),
    beneficiaryOptionId: readNullableString(value['beneficiaryOptionId']),
    visibility: readVisibility(value['visibility']) ?? 'private',
    items: readCreateItems(value['items']),
  };
}

function readCreateItems(value: unknown): readonly WishlistCreatePersistentItem[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((candidate: unknown): WishlistCreatePersistentItem | null => toCreateItem(candidate))
    .filter((item: WishlistCreatePersistentItem | null): item is WishlistCreatePersistentItem => item !== null);
}

function toCreateItem(value: unknown): WishlistCreatePersistentItem | null {
  if (!isRecord(value)) {
    return null;
  }

  const title: string = readString(value['title']);

  if (title.trim().length === 0) {
    return null;
  }

  return {
    id: readString(value['id']) || `item-${title.trim().toLocaleLowerCase('pl-PL')}`,
    title,
    description: readString(value['description']),
    storeUrl: readString(value['storeUrl']),
    priceAmount: readString(value['priceAmount']),
    priority: readItemPriority(value['priority']) ?? 'medium',
  };
}

function readOverviewSection(value: unknown): WishlistOverviewSectionMemoryKey | null {
  return WISHLIST_OVERVIEW_SECTIONS.includes(value as WishlistOverviewSectionMemoryKey)
    ? (value as WishlistOverviewSectionMemoryKey)
    : null;
}

function readCreateStep(value: unknown): WishlistCreateStep | null {
  return WISHLIST_CREATE_STEPS.includes(value as WishlistCreateStep) ? (value as WishlistCreateStep) : null;
}

function readVisibility(value: unknown): WishlistVisibility | null {
  return WISHLIST_VISIBILITIES.includes(value as WishlistVisibility) ? (value as WishlistVisibility) : null;
}

function readCreateOccasion(value: unknown): WishlistCreateOccasion | null {
  return WISHLIST_CREATE_OCCASIONS.includes(value as WishlistCreateOccasion)
    ? (value as WishlistCreateOccasion)
    : null;
}

function readItemPriority(value: unknown): WishlistCreateItemPriority | null {
  return WISHLIST_ITEM_PRIORITIES.includes(value as WishlistCreateItemPriority)
    ? (value as WishlistCreateItemPriority)
    : null;
}

function readString(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function readNullableString(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }

  return value.trim().length > 0 ? value : null;
}

function isRecord(value: unknown): value is Readonly<Record<string, unknown>> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
